import { useCallback, useState } from 'react'
import { captureBrowserScreenshot } from 'utils/screenshotUtils'
import { FeedbackSubmissionData, useFeedbackScreenshotSubmission, useFeedbackSubmission } from './useFeedback'

export type { FeedbackSubmissionData }

export interface FeedbackFormOptions {
  featureType: 'auto_design' | 'ada_chat'
  onSuccess?: (skipIsSubmitted: boolean, feedbackId?: number) => void
  onError?: (error: Error) => void
  initialFeedbackId?: number | null
  // Custom behavior overrides
  thumbsUpBehavior?: 'submit' | 'showChips' | 'submitAndShowChips' | 'submitSilentlyAndShowChips'
  thumbsDownBehavior?: 'submit' | 'showChips' | 'submitAndShowChips' | 'submitSilentlyAndShowChips'
  // Custom submission handler
  customSubmitHandler?: (
    isPositive: boolean,
    selectedChips: string[],
    customText: string
  ) => Promise<Partial<FeedbackSubmissionData>>
  enableOptionalScreenshot?: boolean
  screenshotOptions?: {
    backgroundFill?: string
    compressionLevel?: number
  }
  onShowChips?: () => void
}

export interface FeedbackFormState {
  isPositive: boolean | null
  showChipSelection: boolean
  selectedChips: string[]
  customFeedbackText: string
  isSubmitted: boolean
  feedbackId: number | null
  showScreenshotPrompt: boolean
  isCapturingScreenshot: boolean
  justSubmitted: boolean
}

export interface FeedbackFormActions {
  handleThumbsClick: (isPositiveValue: boolean) => Promise<void>
  handleChipToggle: (chip: string) => void
  handleChipSubmit: () => Promise<void>
  handleCancel: () => void
  handleSubmit: (
    isPositiveValue: boolean,
    selectedChipsArray?: string[],
    silent?: boolean,
    skipIsSubmitted?: boolean,
    additionalData?: Partial<FeedbackSubmissionData>
  ) => Promise<void>
  reset: () => void
  handleScreenshotPrompt: (accept: boolean) => Promise<void>
  dismissScreenshotPrompt: () => void
}

const CUSTOM_CHIP_KEY = 'Something Else'

export const useFeedbackForm = (options: FeedbackFormOptions) => {
  const [isPositive, setIsPositive] = useState<boolean | null>(null)
  const [showChipSelection, setShowChipSelection] = useState(false)
  const [selectedChips, setSelectedChips] = useState<string[]>([])
  const [customFeedbackText, setCustomFeedbackText] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [feedbackId, setFeedbackId] = useState<number | null>(options.initialFeedbackId || null)
  const [showScreenshotPrompt, setShowScreenshotPrompt] = useState(false)
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false)
  const [justSubmitted, setJustSubmitted] = useState(false)

  const { submitFeedback, isSubmitting } = useFeedbackSubmission({
    featureType: options.featureType,
    onSuccess: (skipIsSubmitted, newFeedbackId) => {
      if (newFeedbackId && !feedbackId) {
        setFeedbackId(newFeedbackId)
      }

      if (!options.enableOptionalScreenshot || skipIsSubmitted) {
        setIsSubmitted(!skipIsSubmitted)
      }

      options.onSuccess?.(skipIsSubmitted, newFeedbackId)
    },
    onError: (error: Error) => {
      setIsSubmitted(true)
      setShowChipSelection(false)
      setShowScreenshotPrompt(false)
      setJustSubmitted(false)

      options.onError?.(error)
    },
  })

  const { submitScreenshot, isSubmittingScreenshot } = useFeedbackScreenshotSubmission({
    onSuccess: (feedbackId) => {},
    onError: (error) => {
      console.error('Screenshot update failed:', error)
    },
  })

  const processTextContent = useCallback(
    (selectedChipsArray: string[]) => {
      return selectedChipsArray.includes(CUSTOM_CHIP_KEY) && customFeedbackText.trim() ? customFeedbackText.trim() : ''
    },
    [customFeedbackText]
  )

  const processSelectedOptions = useCallback((selectedChipsArray: string[]) => {
    return selectedChipsArray.filter((chip) => chip !== CUSTOM_CHIP_KEY).join(', ')
  }, [])

  const handleSubmit = useCallback(
    async (
      isPositiveValue: boolean,
      selectedChipsArray: string[] = [],
      silent: boolean = false,
      skipIsSubmitted: boolean = false,
      additionalData: Partial<FeedbackSubmissionData> = {}
    ) => {
      if (isSubmitting) return

      const textContent = processTextContent(selectedChipsArray)
      const selectedOptions = processSelectedOptions(selectedChipsArray)

      let customData: Partial<FeedbackSubmissionData> = {}
      if (options.customSubmitHandler) {
        try {
          customData = await options.customSubmitHandler(isPositiveValue, selectedChipsArray, customFeedbackText)
        } catch (error) {
          console.error('Error in custom submit handler:', error)
        }
      }

      const feedbackData: Partial<FeedbackSubmissionData> = {
        is_positive: isPositiveValue,
        text_content: textContent,
        selected_feedback_options: selectedOptions,
        feedback_id: feedbackId || undefined,
        feedback_metadata: {
          selected_feedback_options: selectedChipsArray,
          custom_feedback_text: customFeedbackText.trim(),
          ...additionalData.feedback_metadata,
          ...customData.feedback_metadata,
        },
        ...additionalData,
        ...customData,
      }

      await submitFeedback(feedbackData, silent, skipIsSubmitted)

      if (options.enableOptionalScreenshot && !silent && !skipIsSubmitted) {
        setJustSubmitted(true)
        setShowScreenshotPrompt(true)
      }
    },
    [
      isSubmitting,
      processTextContent,
      processSelectedOptions,
      feedbackId,
      customFeedbackText,
      submitFeedback,
      options.customSubmitHandler,
      options.enableOptionalScreenshot,
    ]
  )

  const handleThumbsClick = useCallback(
    async (isPositiveValue: boolean) => {
      setIsPositive(isPositiveValue)

      const behavior = isPositiveValue
        ? options.thumbsUpBehavior || 'submit'
        : options.thumbsDownBehavior || 'submitAndShowChips'

      if (showChipSelection) {
        setShowChipSelection(false)
        setSelectedChips([])
      }

      switch (behavior) {
        case 'submit':
          await handleSubmit(isPositiveValue, [], false, false)
          break
        case 'showChips':
          setShowChipSelection(true)
          options.onShowChips?.()
          break
        case 'submitAndShowChips':
          await handleSubmit(isPositiveValue, [], true, false)
          setShowChipSelection(true)
          options.onShowChips?.()
          break
        case 'submitSilentlyAndShowChips':
          await handleSubmit(isPositiveValue, [], true, true)
          setShowChipSelection(true)
          options.onShowChips?.()
          break
      }
    },
    [handleSubmit, options.thumbsUpBehavior, options.thumbsDownBehavior, options.onShowChips]
  )

  const handleChipToggle = useCallback((chip: string) => {
    setSelectedChips((prev) => (prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip]))
  }, [])

  const handleChipSubmit = useCallback(async () => {
    await handleSubmit(isPositive || false, selectedChips, false, false)
    setShowChipSelection(false)
  }, [isPositive, selectedChips, handleSubmit])

  const handleCancel = useCallback(() => {
    setShowChipSelection(false)
    setSelectedChips([])
    setIsPositive(null)
  }, [])

  const reset = useCallback(() => {
    setIsPositive(null)
    setShowChipSelection(false)
    setSelectedChips([])
    setCustomFeedbackText('')
    setIsSubmitted(false)
    setFeedbackId(null)
    setShowScreenshotPrompt(false)
    setIsCapturingScreenshot(false)
    setJustSubmitted(false)
  }, [])

  const handleScreenshotPrompt = useCallback(
    async (accept: boolean) => {
      setShowScreenshotPrompt(false)
      setJustSubmitted(false)

      if (!accept) {
        setIsSubmitted(true)
        options.onSuccess?.(true, feedbackId || undefined)
        return
      }

      setIsCapturingScreenshot(true)

      try {
        const screenshotData = await captureBrowserScreenshot(options.screenshotOptions || {})

        // Only submit screenshot if we actually have data (skip if permission was rejected)
        if (feedbackId && screenshotData) {
          await submitScreenshot(feedbackId, screenshotData, true)
        }
      } catch (error) {
        console.error('Failed to capture screenshot:', error)
      } finally {
        setIsCapturingScreenshot(false)
        setIsSubmitted(true)
        options.onSuccess?.(true, feedbackId || undefined)
      }
    },
    [feedbackId, options.screenshotOptions, submitScreenshot]
  )

  const dismissScreenshotPrompt = useCallback(() => {
    setShowScreenshotPrompt(false)
    setJustSubmitted(false)
    setIsSubmitted(true)
  }, [])

  const state: FeedbackFormState = {
    isPositive,
    showChipSelection,
    selectedChips,
    customFeedbackText,
    isSubmitted,
    feedbackId,
    showScreenshotPrompt,
    isCapturingScreenshot,
    justSubmitted,
  }

  const actions: FeedbackFormActions = {
    handleThumbsClick,
    handleChipToggle,
    handleChipSubmit,
    handleCancel,
    handleSubmit,
    reset,
    handleScreenshotPrompt,
    dismissScreenshotPrompt,
  }

  return {
    state,
    actions,
    isSubmitting,
    isSubmittingScreenshot,
    setCustomFeedbackText,
  }
}
